import type { QuestionId } from "./survey";

/**
 * A variation is a single audience-specific funnel. The SPA mounts a custom
 * page tree at /:slug, but every variation shares the survey question pool
 * and the tracking schema so the dashboard can compare apples-to-apples.
 *
 * Add new variations here AND add a route handler in apps/web/src/variations/.
 */
export interface VariationDefinition {
  slug: string;
  /** Human label for the dashboard. */
  name: string;
  /** Marketer-facing description / audience. */
  audience: string;
  /** Ordered list of question IDs this variation asks. */
  questions: QuestionId[];
  /** Paddle Billing price IDs for the paywall. */
  paddle: {
    monthlyPriceId: string;
    annualPriceId: string;
    /** Discount banner copy, if any. */
    discountBadge?: string;
  };
  /**
   * Optional explicit funnel step list for the dashboard. If omitted, the
   * dashboard derives the funnel from the question list bracketed by
   * intro → loading → paywall → success. Variations with custom
   * interstitials should declare them here so the funnel chart matches the
   * real user journey.
   */
  funnelSteps?: { step: string; label: string }[];
  /** When the marketer paused the variation, the SPA shows a redirect. */
  status: "active" | "paused";
}

export const VARIATIONS: Record<string, VariationDefinition> = {
  "productivity-archetype": {
    slug: "productivity-archetype",
    name: "Productivity Archetype Quiz",
    audience:
      "Broad cold traffic — psychographic-driven landing leveraging archetype reveal",
    /** Ordered to match the 12-phase funnel in the brief. Non-question
     *  screens (validation, social proof, archetype reveal, paywall) are
     *  declared inside apps/web/src/variations/variation-a/steps.ts. */
    questions: [
      "gender",
      "age_range",
      "professional_context",
      "overwhelm",
      "focus_attention",
      "memory",
      "emotional_regulation",
      "morning_routine",
      "motivation_source",
      "organization_style",
      "shiny_object",
      "primary_goal_30d",
      "secondary_desire",
      "feature_pref",
      "past_attempts",
      "barrier",
      "magic_wand",
      "visualization_30d",
      "email",
    ],
    paddle: {
      // Habitify Pro · Monthly · $8.99/mo · no trial
      monthlyPriceId: "pri_01k82y2qceqatg3r2gr9p5t09t",
      // Habitify Pro · Yearly · $59.99/yr · 7-day free trial
      annualPriceId: "pri_01k82xxr5dgcdm4ekqek65m4f2",
      // Badge text is auto-derived: defaults to "7 DAYS FREE" when omitted.
      // Override here only if a marketing campaign needs different copy.
    },
    funnelSteps: [
      { step: "intro", label: "Intro" },
      { step: "question_gender", label: "Q: Gender" },
      { step: "question_age_range", label: "Q: Age range" },
      { step: "question_professional_context", label: "Q: Daily environment" },
      { step: "question_overwhelm", label: "Q: End-of-day feeling" },
      { step: "question_focus_attention", label: "Q: Focus / start" },
      { step: "question_memory", label: "Q: Memory" },
      { step: "question_emotional_regulation", label: "Q: Streak reaction" },
      { step: "validation", label: "Validation (78%)" },
      { step: "question_morning_routine", label: "Q: Morning routine" },
      { step: "question_motivation_source", label: "Q: Motivation source" },
      { step: "question_organization_style", label: "Q: Organization style" },
      { step: "question_shiny_object", label: "Q: Shiny-object syndrome" },
      { step: "big_data", label: "Authority — Big data" },
      { step: "science_promise", label: "Authority — Science" },
      { step: "question_primary_goal_30d", label: "Q: 30-day primary goal" },
      { step: "question_secondary_desire", label: "Q: Why" },
      { step: "question_feature_pref", label: "Q: Support preference" },
      { step: "question_past_attempts", label: "Q: Past attempts" },
      { step: "question_barrier", label: "Q: Barrier" },
      { step: "community_reviews", label: "Community reviews" },
      { step: "question_magic_wand", label: "Q: Magic wand" },
      { step: "question_visualization_30d", label: "Q: 30-day visualization" },
      { step: "calculating", label: "Calculating plan" },
      { step: "archetype_reveal", label: "Archetype reveal" },
      { step: "shadow_side", label: "Shadow side" },
      { step: "social_proof_closer", label: "Final social proof" },
      { step: "question_email", label: "Q: Email capture" },
      { step: "paywall", label: "Paywall" },
      // The pre-checkout signup sheet is its own step in the real
      // funnel — without a row here the dashboard collapses "tapped
      // Start trial" through "Paddle completed" into a single bar and
      // hides the auth-drop-off (where many users actually bail).
      { step: "signup_modal", label: "Pre-checkout signup" },
      { step: "success", label: "Purchase confirmed" },
    ],
    status: "active",
  },

  "hyperfocus-protocol": {
    slug: "hyperfocus-protocol",
    name: "Hyperfocus Protocol",
    audience:
      "Knowledge workers + creators chasing deep focus / hyperfocus. Pitch leans 21-day protocol + science-of-attention angle.",
    // Order tuned from production funnel data (the original lost 78%
    // at intro→Q1 with a name text-input first). We now LEAD with a
    // one-tap emotional question that mirrors the ad hook ("your brain
    // hates starting"), defer demographics until the user's invested,
    // and capture NO name / email in-quiz (email is taken at the
    // pre-checkout signup instead) to strip keyboard friction.
    questions: [
      // Hook — one-tap emotional questions first (match the ad pain)
      "intention_trap",
      "doomscroll",
      "big_task_response",
      "fake_work",
      "perfectionism_block",
      // Demographics — low-friction taps, once they're rolling
      "gender",
      "age_range",
      // Phase 1B — Behavioral Science
      "low_energy_time",
      "mood_dependent",
      "tab_count",
      "overthinking",
      "flow_frequency",
      // Phase 2 — Goals
      "one_problem",
      "success_30d_focus",
      "shift_potential",
      // Phase 3 — Past
      "past_methods",
      "past_stickiness",
      "past_failure",
      // Phase 5 — Future
      "future_shift",
      "motivation_category",
      // Loading interrupts
      "commit_60s",
      "system_unlock",
    ],
    paddle: {
      // Same Habitify Pro product. A separate price-id can be wired
      // here later for variation-specific experiments.
      monthlyPriceId: "pri_01k82y2qceqatg3r2gr9p5t09t",
      annualPriceId: "pri_01k82xxr5dgcdm4ekqek65m4f2",
    },
    funnelSteps: [
      // Merged hook + Q1 lead. The ad-hook copy and the
      // intention_trap chips live on a single above-the-fold screen
      // — tapping an answer chip is both the first conversion event
      // (`view_step` → `answer_question`) and the advance to Q2.
      // Replaces the old separate `intro` + `question_intention_trap`
      // pair, which lost ~78% on a free "Continue" tap. The legacy
      // pair is still reachable via `?intro=full` for QA but is not
      // listed here — the dashboard funnel reflects production
      // traffic only.
      { step: "hook_question", label: "Hook + Q: Intention trap" },
      { step: "question_doomscroll", label: "Q: Doomscroll frequency" },
      { step: "question_big_task_response", label: "Q: Big-task response" },
      { step: "question_fake_work", label: "Q: Fake work" },
      { step: "question_perfectionism_block", label: "Q: Perfectionism block" },
      { step: "task_paralysis", label: "Authority — Task paralysis" },
      // Demographics deferred to here
      { step: "question_gender", label: "Q: Gender" },
      { step: "question_age_range", label: "Q: Age range" },
      // Phase 1B
      { step: "question_low_energy_time", label: "Q: Low-energy time" },
      { step: "question_mood_dependent", label: "Q: Mood-dependent" },
      { step: "question_tab_count", label: "Q: Tab count" },
      { step: "question_overthinking", label: "Q: Overthinking" },
      { step: "question_flow_frequency", label: "Q: Flow frequency" },
      { step: "success_not_random", label: "Authority — Success not random" },
      // Phase 2 + personalizing
      { step: "question_one_problem", label: "Q: One problem" },
      { step: "question_success_30d_focus", label: "Q: 30-day success" },
      { step: "question_shift_potential", label: "Q: Shift potential" },
      { step: "personalizing_plan", label: "Authority — Personalizing plan" },
      // Phase 3 — Past
      { step: "question_past_methods", label: "Q: Past methods" },
      { step: "question_past_stickiness", label: "Q: Past stickiness" },
      { step: "question_past_failure", label: "Q: Past failure reason" },
      { step: "standard_lists_drain", label: "Authority — Lists drain willpower" },
      // Phase 4 + 5
      { step: "value_proposition", label: "Authority — Science-backed bridge" },
      { step: "question_future_shift", label: "Q: 30-day future shift" },
      { step: "question_motivation_category", label: "Q: Motivation category" },
      // Loading interrupts
      { step: "loading", label: "Calculating plan" },
      { step: "question_commit_60s", label: "Q: Commit 60 seconds" },
      { step: "question_system_unlock", label: "Q: System unlock belief" },
      // Result
      { step: "wellbeing_summary", label: "Result — Focus diagnosis" },
      { step: "timeline_roadmap", label: "Result — Timeline roadmap" },
      { step: "path_to_focus", label: "Result — Path to focus" },
      // No email step — email captured at signup_modal (pre-checkout)
      { step: "paywall", label: "Paywall" },
      { step: "signup_modal", label: "Pre-checkout signup" },
      { step: "success", label: "Purchase confirmed" },
    ],
    status: "active",
  },

  "hyperfocus-protocol-v2": {
    slug: "hyperfocus-protocol-v2",
    name: "Hyperfocus Protocol v2",
    audience:
      "Meta/IG cold traffic for the 'your brain hates starting' hyperfocus creative. Design-handoff funnel: richer Liven-grade paywall (discount reveal + countdown + now→21-day comparison + per-day pricing + press/testimonials/guarantee), email lead-capture before the result reveal, and a real multi-signal habit-set match.",
    // Ordered to match the v2 design's STEPS. Demographics lead (the
    // design opens intro → gender), email is captured before the
    // result reveal as a lead-gen step, and the matcher reads the
    // q_problem / q_success / behavioral answers below.
    questions: [
      "gender",
      "age",
      "q_intentions",
      "q_scroll",
      "q_bigtask",
      "q_busy",
      "q_perfection",
      "q_energy",
      "q_mood",
      "q_tabs",
      "q_overthink",
      "q_flow",
      "q_problem",
      "q_success",
      "q_shift",
      "q_tried",
      "q_stick",
      "q_why",
      "q_impact",
      "q_motivation",
    ],
    paddle: {
      // Same Habitify Pro product as the other variations. A
      // v2-specific price-id can be wired here later for a
      // pricing experiment.
      monthlyPriceId: "pri_01k82y2qceqatg3r2gr9p5t09t",
      annualPriceId: "pri_01k82xxr5dgcdm4ekqek65m4f2",
    },
    funnelSteps: [
      { step: "intro", label: "Intro — Hyperfocus" },
      // Foundation
      { step: "question_gender", label: "Q: Gender" },
      { step: "question_age", label: "Q: Age" },
      // Phase 1A — Mental roadblocks
      { step: "question_q_intentions", label: "Q: Intention trap" },
      { step: "question_q_scroll", label: "Q: Doomscroll" },
      { step: "question_q_bigtask", label: "Q: Big-task response" },
      { step: "question_q_busy", label: "Q: Busy vs productive" },
      { step: "question_q_perfection", label: "Q: Perfectionism block" },
      { step: "social_paralysis", label: "Social — Task paralysis (78%)" },
      // Phase 1B — Behavioral science
      { step: "question_q_energy", label: "Q: Low-energy time" },
      { step: "question_q_mood", label: "Q: Mood-dependent" },
      { step: "question_q_tabs", label: "Q: Tab count" },
      { step: "question_q_overthink", label: "Q: Overthinking" },
      { step: "question_q_flow", label: "Q: Flow frequency" },
      { step: "social_calculated", label: "Social — Success is calculated" },
      // Phase 2 — Goals
      { step: "question_q_problem", label: "Q: One problem" },
      { step: "question_q_success", label: "Q: 30-day success" },
      { step: "question_q_shift", label: "Q: Shift potential" },
      { step: "trust_personalize", label: "Loading — Personalizing plan" },
      // Phase 3 — History
      { step: "question_q_tried", label: "Q: Methods tried" },
      { step: "question_q_stick", label: "Q: Stickiness" },
      { step: "question_q_why", label: "Q: Why they failed" },
      { step: "trust_makesense", label: "Social — Lists drain willpower" },
      // Phase 4 — Value prop
      { step: "valueprop", label: "Value — Science-backed bridge" },
      // Phase 5 — Motivation
      { step: "question_q_impact", label: "Q: Biggest-impact shift" },
      { step: "question_q_motivation", label: "Q: Motivation category" },
      { step: "building", label: "Loading — Building plan" },
      // Email lead capture before the reveal
      { step: "email_capture", label: "Email capture" },
      // Result reveal
      { step: "result", label: "Result — Well-being summary" },
      { step: "roadmap", label: "Result — 21-day roadmap" },
      { step: "chart", label: "Result — Path to peak focus" },
      // Conversion
      { step: "paywall", label: "Paywall" },
      // No signup step any more — tapping the trial CTA silently
      // auto-creates the account (email from the quiz + generated
      // password, mailed via Postmark) and goes straight to Paddle.
      // The `signup_modal` event still fires on that tap, so it now
      // measures "tapped Start trial → Paddle" rather than a sheet the
      // user sees. Keeping the event id preserves funnel continuity
      // across the modal → auto-signup switchover.
      { step: "signup_modal", label: "Tapped Start trial" },
      { step: "success", label: "Purchase confirmed" },
    ],
    status: "active",
  },

  "hyperfocus-protocol-question-first": {
    slug: "hyperfocus-protocol-question-first",
    name: "Hyperfocus Protocol — Question First",
    audience:
      "A/B challenger to hyperfocus-protocol-v2: identical funnel, but the entry skips the landing/intro splash and opens directly on the gender question (photo cards + a folded value-prop opener) to recover the ~80% first-screen drop on cold paid traffic.",
    // Same question set + matcher as v2 — the only difference is the
    // first screen (question-first entry vs the intro splash).
    questions: [
      "gender",
      "age",
      "q_intentions",
      "q_scroll",
      "q_bigtask",
      "q_busy",
      "q_perfection",
      "q_energy",
      "q_mood",
      "q_tabs",
      "q_overthink",
      "q_flow",
      "q_problem",
      "q_success",
      "q_shift",
      "q_tried",
      "q_stick",
      "q_why",
      "q_impact",
      "q_motivation",
    ],
    paddle: {
      monthlyPriceId: "pri_01k82y2qceqatg3r2gr9p5t09t",
      annualPriceId: "pri_01k82xxr5dgcdm4ekqek65m4f2",
    },
    // Identical to hyperfocus-protocol-v2 minus the `intro` row — the
    // funnel opens on `question_gender`.
    funnelSteps: [
      { step: "question_gender", label: "Q: Gender (entry)" },
      { step: "question_age", label: "Q: Age" },
      { step: "question_q_intentions", label: "Q: Intention trap" },
      { step: "question_q_scroll", label: "Q: Doomscroll" },
      { step: "question_q_bigtask", label: "Q: Big-task response" },
      { step: "question_q_busy", label: "Q: Busy vs productive" },
      { step: "question_q_perfection", label: "Q: Perfectionism block" },
      { step: "social_paralysis", label: "Social — Task paralysis (78%)" },
      { step: "question_q_energy", label: "Q: Low-energy time" },
      { step: "question_q_mood", label: "Q: Mood-dependent" },
      { step: "question_q_tabs", label: "Q: Tab count" },
      { step: "question_q_overthink", label: "Q: Overthinking" },
      { step: "question_q_flow", label: "Q: Flow frequency" },
      { step: "social_calculated", label: "Social — Success is calculated" },
      { step: "question_q_problem", label: "Q: One problem" },
      { step: "question_q_success", label: "Q: 30-day success" },
      { step: "question_q_shift", label: "Q: Shift potential" },
      { step: "trust_personalize", label: "Loading — Personalizing plan" },
      { step: "question_q_tried", label: "Q: Methods tried" },
      { step: "question_q_stick", label: "Q: Stickiness" },
      { step: "question_q_why", label: "Q: Why they failed" },
      { step: "trust_makesense", label: "Social — Lists drain willpower" },
      { step: "valueprop", label: "Value — Science-backed bridge" },
      { step: "question_q_impact", label: "Q: Biggest-impact shift" },
      { step: "question_q_motivation", label: "Q: Motivation category" },
      { step: "building", label: "Loading — Building plan" },
      { step: "email_capture", label: "Email capture" },
      { step: "result", label: "Result — Well-being summary" },
      { step: "roadmap", label: "Result — 21-day roadmap" },
      { step: "chart", label: "Result — Path to peak focus" },
      { step: "paywall", label: "Paywall" },
      // Auto-signup at checkout (no sheet) — see hyperfocus-protocol-v2.
      // `signup_modal` now = "tapped Start trial → Paddle".
      { step: "signup_modal", label: "Tapped Start trial" },
      { step: "success", label: "Purchase confirmed" },
    ],
    status: "active",
  },
};

export const VARIATION_SLUGS = Object.keys(VARIATIONS);

export function getVariation(slug: string): VariationDefinition | undefined {
  return VARIATIONS[slug];
}
